import { defineRouting } from "next-intl/routing";

/**
 * i18n routing. `localePrefix: "as-needed"` keeps English at the current
 * (unprefixed) URLs — so existing SEO + the PPR edge cache are undisturbed —
 * while Vietnamese lives under /vi and French under /fr, each rendering as its
 * own statically-cached page with proper hreflang. EN → VI → FR is the rollout
 * order; FR ships after VI translations land.
 */
export const routing = defineRouting({
  // ⚠️ THIS LIST IS THE VOCABULARY FOR THE WHOLE CODEBASE. `Locale`
  // (`lib/locale.ts`) derives from it, so adding an entry here turns every
  // unanswered `Record<Locale, …>` into a compile error on purpose. That is the
  // only mechanism that reaches the label maps, which fail soft otherwise.
  //
  // ⛔ A locale here is ROUTABLE but not necessarily INDEXABLE or OFFERED.
  // Three separate lists, and they are meant to differ mid-rollout:
  //   routing.locales     -> /zh-Hans/… resolves and prerenders
  //   HREFLANG_LOCALES    -> it may be indexed and advertised (seo/alternates)
  //   LanguageSwitcher    -> a reader can actually choose it
  // Today all five locales are in all three lists (Chinese joined the second
  // and third 2026-09-23, when its message files filled). A new locale joins
  // the FIRST alone: its tree renders English, is noindexed by the [locale]
  // layout, and nothing links to it until its messages are done.
  //
  // Script, not region, is the Chinese axis: `zh-Hans`/`zh-Hant` are the
  // correct hreflang values and one file serves Taiwan and Hong Kong alike.
  // ⛔ Never add a bare `zh` beside them.
  locales: ["en", "vi", "fr", "zh-Hans", "zh-Hant"],
  defaultLocale: "en",
  localePrefix: "as-needed",
  // Suppress next-intl's `Link: rel="alternate" hreflang` RESPONSE header. We
  // emit hreflang from our own `buildAlternates()` in the HTML <head>, and the
  // two must not disagree: next-intl's header is built from the request host
  // (so on easytiger.netlify.app it advertises netlify.app alternates — a
  // cross-domain duplicate signal) and it lists /fr, which we deliberately omit
  // from the HTML set while fr.json is unfinished. One source of truth = HTML.
  alternateLinks: false,
  // The URL decides the locale — nothing else (CEO-decided 2026-07-16).
  //
  // Default-ON detection made the `NEXT_LOCALE` cookie (set by any /vi visit,
  // sticky for a year) and `Accept-Language` authoritative over the page you
  // were actually reading: an unprefixed URL 307'd to /vi. Reading English and
  // clicking a footer link bounced you to Vietnamese, because an EN page's
  // hrefs are unprefixed by definition and middleware honoured the cookie over
  // the page. It also caused the "shared EN link previews with a VI card"
  // gotcha (a vi-locale scraper followed the redirect and read /vi's OG card).
  //
  // ONLY SAFE ALONGSIDE `LocaleLink` — VI pages must render /vi-prefixed hrefs
  // themselves, because middleware will no longer rescue an unprefixed link
  // with a redirect. Turn this back on and you resurrect the cookie hijack;
  // remove LocaleLink and VI users fall out of /vi on every click.
  //
  // THE TRADE, accepted knowingly: a Vietnamese browser typing easytiger.vn now
  // gets EN and must use the switcher. Organic traffic is unaffected (hreflang
  // already serves /vi to Vietnamese searchers), so this costs auto-VI only on
  // typed and shared links — including the pro-recruitment funnel.
  localeDetection: false,

  // ⛔ NO NEXT_LOCALE COOKIE. ONE Set-Cookie WAS MAKING EVERY HTML PAGE ON THE
  // SITE UNCACHEABLE. Measured on production 2026-09-06 (`/about`, repeat
  // requests):
  //   set-cookie:    NEXT_LOCALE=en; Path=/; SameSite=lax
  //   cache-control: private,no-cache,no-store,max-age=0,must-revalidate
  //   cache-status:  "Netlify Durable"; fwd=bypass, "Netlify Edge"; fwd=miss
  //   x-nextjs-postponed: 1      ← Next DID prerender a PPR shell
  //   x-nextjs-stale-time: 300   ← and asked for a 300s stale window
  // A response carrying Set-Cookie is `private`, so both CDN layers bypassed on
  // 100% of HTML requests, including anonymous crawler hits on static marketing
  // pages. Cost, on one reused TCP connection: a cached JS chunk 60ms vs
  // `/about` 399/510/708ms — ~450ms of origin compute per request that the
  // whole PPR + `use cache` investment exists to avoid.
  //
  // ⭐ Safe because the cookie was already inert HERE: `localeDetection` is
  // false directly above, so next-intl never consulted it to choose a locale —
  // the URL decides. Turning the WRITE off simply stops it advertising a
  // decision nothing reads.
  //
  // ⚠️ `setLocaleClient` (lib/locale.ts) still writes NEXT_LOCALE client-side.
  // That write is now vestigial rather than load-bearing — keep or remove it on
  // its own merits, but do NOT re-enable this to "fix" it: re-enabling makes
  // every page private again. The "stuck on VI" bug it was written for is
  // already prevented by `localeDetection: false`, which is a stronger guard.
  //
  // ⛔ Do NOT try to strip the Set-Cookie in `proxy.ts` instead — that was the
  // first attempt and it does not work. `NextResponse` owns a ResponseCookies
  // jar bound to its headers and re-serialises it after any manual
  // `headers.delete`/`append`, so the cookie comes back (verified against a
  // freshly restarted dev server). This is the supported switch.
  localeCookie: false,
});

export type AppLocale = (typeof routing.locales)[number];

/**
 * Matches a leading non-default locale segment (`/vi`, `/fr`) at a segment
 * boundary. EN stays unprefixed (`localePrefix: "as-needed"`) so it needs no
 * stripping. Client-safe — derived purely from `routing.locales`.
 */
export const LOCALE_PREFIX_RE = new RegExp(
  `^/(${routing.locales.filter((l) => l !== routing.defaultLocale).join("|")})(?=/|$)`,
);

/**
 * Strip the locale prefix from a pathname so route matching is locale-agnostic.
 * `/vi/insights` → `/insights`, `/vi` → `/`, `/insights` → `/insights`.
 * Use this for any active-link / route-prefix check that compares an
 * unprefixed `href` against `usePathname()` (which includes the `/vi` prefix).
 */
export function stripLocalePrefix(pathname: string): string {
  return pathname.replace(LOCALE_PREFIX_RE, "") || "/";
}
